import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/shared/Modal/Modal';
import CommissionAddDevicesScreen from './CommissionAddDevicesScreen/CommissionAddDevicesScreen';
import CommissionCreateSiteScreen from './CommissionCreateSiteScreen/CommissionCreateSiteScreen';
import CommissionBeamSelectionScreen from './CommissionBeamSelectionScreen/CommissionBeamSelectionScreen';
import RasterProgressScreen from './RasterProgressScreen/RasterProgressScreen';
import CommissionHBFServiceScreen from './CommissionHBFServiceScreen/CommissionHBFServiceScreen';
import CommissionSuccessScreen from './CommissionSuccessScreen/CommissionSuccessScreen';
import { getCommissioningStatus } from 'apis/commissioningApis/InitiateCommissioning';
import CommissionErrorBox from './CommissionErrorBox/CommissionErrorBox';
import {
  getRasterHeatmapInfo,
  doInitiateCommissioning,
  doCreateBundle,
  doTerminateCommissionOp,
  doDeleteBundle,
  updateBundleStatus,
  updateStatus,
  doPatchBundle
} from 'redux/actions';
import './CreateSiteModal.css';
import { Status } from 'common/utils/constants';
import { SCREEN_INDEXES, CLOSE_MODAL_SECONDS } from './utils';

const CreateSiteModal = (props) => {
  const dispatch = useDispatch();
  const [selectedDevices, setSelectedDevices] = useState([{}, {}, {}]);
  const [screenIndex, setScreenIndex] = useState(SCREEN_INDEXES.ADD_DEVICES);
  const [localCommissioningId, setLocalCommissioningId] = useState(null);
  const [customStatus, setCustomStatus] = useState(null);
  const [newBundleId, setNewBundleId] = useState(null);
  const [createBundleErrorCode, setCreateBundleErrorCode] = useState(null);
  const [patchBundleErrorCode, setPatchBundleErrorCode] = useState(null);
  const [commissioningErrorCode, setCommissioningErrorCode] = useState(null);
  const [getStatusErrorMsg, setGetStatusErrorMsg] = useState(null);
  const [commissioningProgress, setCommissioningProgress] = useState(0);
  const [commissioningStatusUri, setCommissioningStatusUri] = useState(null);
  const [recommBundleId, setRecommBundleId] = useState(null);
  const [terminateCommissionOpErrorCode, setTerminateCommissionOpErrorCode] = useState(null);
  const [deleteBundleErrorCode, setDeleteBundleErrorCode] = useState(null);
  const [timeCount, setTimeCount] = useState(0);

  const autoCloseModalTimeout = useRef(null);
  const statusRunner = useRef(null);
  const statusRunner2 = useRef(null);
  const autoNextScreenTimeout = useRef(null);

  const terminateCommissioningStatusName = `terminateCommissionOp-${localCommissioningId}`;
  const terminateCommissioningStatus = useSelector((store) => store.statuses[terminateCommissioningStatusName]);

  const deleteBundleStatusName = `DeleteBundle-${newBundleId ? newBundleId : recommBundleId}`;
  const deleteBundleStatus = useSelector((store) => newBundleId && store.statuses.bundles?.[deleteBundleStatusName]);

  // callback after setting selected devices
  useEffect(() => {
    if (Object.keys(selectedDevices[0]).length === 0) return;
    if (
      customStatus === 'awaiting_activeBeam' &&
      commissioningStatusUri &&
      localCommissioningId &&
      !statusRunner2?.current
    ) {
      statusRunner2.current = setInterval(() => {
        getCommissioningStatus(commissioningStatusUri)
          .then((res) => {
            if (
              res &&
              (res.runtimeStatus === 'Completed' ||
                res.runtimeStatus === 'Terminated' ||
                res.runtimeStatus === 'Failed')
            ) {
              if (res.runtimeStatus === 'Failed' || res?.customStatus?.status === 'error') {
                setGetStatusErrorMsg(res?.customStatus?.message);
              }
              clearInterval(statusRunner2.current);
            }

            setCustomStatus(res.customStatus.status);
            dispatch(updateStatus(`GetCommissioningStatus-${localCommissioningId}`, Status.SUCCESS));
          })
          .catch((error) => {
            dispatch(updateStatus(`GetCommissioningStatus-${localCommissioningId}`, Status.FAILURE));
            console.error(error);
          });
      }, 60000);
    }
  }, [
    selectedDevices,
    localCommissioningId,
    customStatus,
    commissioningErrorCode,
    commissioningProgress,
    screenIndex,
    recommBundleId,
    terminateCommissioningStatus,
    deleteBundleStatus,
    terminateCommissionOpErrorCode,
    deleteBundleErrorCode
  ]);

  // handle updating timeCount and auto-closing modal
  useEffect(() => {
    if (screenIndex === SCREEN_INDEXES.SUCCESS) {
      autoCloseModalTimeout.current = setTimeout(() => {
        if (timeCount < CLOSE_MODAL_SECONDS) {
          setTimeCount(timeCount + 1);
        } else {
          closeModal(SCREEN_INDEXES.SUCCESS);
        }
      }, 1000);
    }

    return () => {
      clearTimeout(autoCloseModalTimeout.current);
    };
  }, [timeCount, screenIndex]);

  const cancel_comm_cb_failed = (e) => {
    console.log('e in cancel_comm_cb_failed', e.message);
    setTerminateCommissionOpErrorCode(e.message);
  };

  const delete_bundle_cb_failed = (e) => {
    console.log('e in delete_bundle_cb_failed', e.message);
    setDeleteBundleErrorCode(e.message);
  };

  const init_comm_cb_success = (res) => {
    let triggerGetRasterInfoAPI = false;
    res.statusQueryGetUri && setCommissioningStatusUri(res.statusQueryGetUri);
    setCommissioningErrorCode(null);
    setLocalCommissioningId(res.commissioningId);
    setScreenIndex(SCREEN_INDEXES.IN_PROGRESS);
    statusRunner.current =
      res.statusQueryGetUri &&
      setInterval(() => {
        getCommissioningStatus(res.statusQueryGetUri)
          .then(async (res) => {
            dispatch(updateStatus(`GetCommissioningStatus-${res.commissioningId}`, Status.SUCCESS));
            if (
              res?.customStatus?.status_info?.progress &&
              res.customStatus.status_info.progress !== commissioningProgress
            ) {
              setCommissioningProgress(res.customStatus.status_info.progress);
              if (res.customStatus.status_info.progress === 100) {
                clearInterval(statusRunner.current);
              }
            }
            if (
              res &&
              (res.runtimeStatus === 'Completed' ||
                res.runtimeStatus === 'Terminated' ||
                res.runtimeStatus === 'Failed')
            ) {
              if (res.runtimeStatus === 'Failed' || res?.customStatus?.status === 'error') {
                setGetStatusErrorMsg(res?.customStatus?.message);
              }
              clearInterval(statusRunner.current);
            } else {
              if (res.customStatus && customStatus !== res.customStatus.status)
                setCustomStatus(res.customStatus.status);

              if (res.customStatus && res.customStatus.status === 'awaiting_activeBeam') {
                // call getRasterInfo API
                clearInterval(statusRunner.current);
                setCustomStatus(res.customStatus.status);
                if (!triggerGetRasterInfoAPI && res.commissioningId) {
                  triggerGetRasterInfoAPI = true;
                  let get_raster_cb_success = () => {
                    autoNextScreenTimeout.current = setTimeout(function () {
                      setScreenIndex(SCREEN_INDEXES.SELECT_BEAM);
                    }, 3000);
                  };
                  dispatch(getRasterHeatmapInfo(res.commissioningId, get_raster_cb_success));
                }
              }
            }
          })
          .catch((error) => {
            setGetStatusErrorMsg(error.message);
            dispatch(updateStatus(`GetCommissioningStatus-${res.commissioningId}`, Status.FAILURE));
            !recommBundleId && newBundleId && dispatch(doDeleteBundle(newBundleId, null, delete_bundle_cb_failed));
            clearInterval(statusRunner.current);
            console.log(error.message);
          });
      }, 4000);
  };

  const init_comm_cb_failed = (error) => {
    setCommissioningErrorCode(error.message);
  };

  const createOrPatchBundleAndInitCommission = (bundleObj) => {
    let cb_success = (result) => {
      let bundleId = result?.id;
      if (bundleId) setNewBundleId(bundleId);

      (bundleId || recommBundleId) &&
        dispatch(
          doInitiateCommissioning(bundleId ? bundleId : recommBundleId, init_comm_cb_success, init_comm_cb_failed)
        );
    };

    let cb_failed = (error) => {
      !recommBundleId ? setCreateBundleErrorCode(error.message) : setPatchBundleErrorCode(error.message);
    };

    if (recommBundleId) {
      console.log('recommBundleId and bundleObj for recommission', recommBundleId, bundleObj);
      dispatch(doPatchBundle(recommBundleId, bundleObj, cb_success, cb_failed));
    } else {
      console.log('bundleObj for commission', bundleObj);
      bundleObj && dispatch(doCreateBundle(bundleObj, cb_success, cb_failed));
    }
  };

  const retryCommissioningOp = () => {
    (newBundleId || recommBundleId) &&
      dispatch(
        doInitiateCommissioning(newBundleId ? newBundleId : recommBundleId, init_comm_cb_success, init_comm_cb_failed)
      );
  };

  const onNext = (index, data) => {
    if (index === SCREEN_INDEXES.ADD_DEVICES) {
      setSelectedDevices(data);
      setScreenIndex(SCREEN_INDEXES.CREATE_SITE);
    } else if (index === SCREEN_INDEXES.CREATE_SITE) {
      // below will trigger create bundle API; once it succeeds
      // it will initiate commissioning along with the new bundleId, and start fetching statuses
      createOrPatchBundleAndInitCommission(data);
    } else if (index === SCREEN_INDEXES.IN_PROGRESS) {
      clearTimeout(autoNextScreenTimeout.current);
      setScreenIndex(SCREEN_INDEXES.SELECT_BEAM);
      // test heatmap screen
      // dispatch(getRasterHeatmapInfo('0efeae87-2be5-4eaa-a9d1-b5ce5e7367fb'));
    } else if (index === SCREEN_INDEXES.SELECT_BEAM || index === SCREEN_INDEXES.HBF_SERVICE) {
      setScreenIndex(SCREEN_INDEXES.SUCCESS);
    } else if (index === SCREEN_INDEXES.SUCCESS) {
      console.log('remote commissioning completes');
    }
  };

  const clearTimeoutAndInterval = () => {
    statusRunner && statusRunner.current && clearInterval(statusRunner.current);
    statusRunner2 && statusRunner2.current && clearInterval(statusRunner2.current);
    autoCloseModalTimeout && autoCloseModalTimeout.current && clearTimeout(autoCloseModalTimeout.current);
  };

  const closeModal = (index) => {
    if (screenIndex === SCREEN_INDEXES.CREATE_SITE) {
      if (commissioningErrorCode && newBundleId) {
        console.log('deleting new bundle');
        dispatch(doDeleteBundle(newBundleId, null, delete_bundle_cb_failed));
      }
    } else if (
      screenIndex === SCREEN_INDEXES.IN_PROGRESS ||
      screenIndex === SCREEN_INDEXES.SELECT_BEAM ||
      screenIndex === SCREEN_INDEXES.HBF_SERVICE
    ) {
      if (localCommissioningId && index !== SCREEN_INDEXES.SUCCESS) {
        console.log('localCommissioningId for deleting comOp', localCommissioningId);
        dispatch(doTerminateCommissionOp(localCommissioningId, null, cancel_comm_cb_failed));
      }
      if (!recommBundleId && newBundleId && index !== SCREEN_INDEXES.SUCCESS) {
        console.log('when deleting bundle: ', newBundleId);
        dispatch(doDeleteBundle(newBundleId, null, delete_bundle_cb_failed));
      }
    } else if (screenIndex === SCREEN_INDEXES.SUCCESS) {
      props.actionsPageThis && props.actionsPageThis.setState({ refreshActionList: true });
    }

    dispatch(updateBundleStatus('CreateBundle', Status.NONE));
    recommBundleId && dispatch(updateBundleStatus(`PatchBundle-${recommBundleId}`, Status.NONE));
    clearTimeoutAndInterval();
    props.onClose();
  };

  let cancelCommissioningErrorBox = null;
  if (terminateCommissioningStatus === Status.FAILURE || terminateCommissionOpErrorCode) {
    dispatch(updateStatus(terminateCommissioningStatusName, 0));
    cancelCommissioningErrorBox = (
      <CommissionErrorBox
        title={`Terminate CommissionOp Error: ${terminateCommissionOpErrorCode}`}
        message={'Something went wrong. Please try again later.'}
        onClose={props.onClose}
      />
    );
  }

  let deleteBundleErrorBox = null;
  if (deleteBundleStatus === Status.FAILURE || deleteBundleErrorCode) {
    dispatch(updateStatus(deleteBundleStatusName, 0));
    setDeleteBundleErrorCode(null);
    deleteBundleErrorBox = (
      <CommissionErrorBox
        title={'Delete bundle error'}
        message={'Something went wrong. Please try again later.'}
        onClose={props.onClose}
      />
    );
  }

  return (
    <Modal
      animateX={false}
      global
      close={closeModal}
      noPadding
      height={'fit-content'}
      width={'fit-content'}
      displayFlex={true}
      overflow={true}
      className={'RemoteCommissioning_VisibleOverflow'}
      // height={738}
      // width={565}
    >
      {cancelCommissioningErrorBox && cancelCommissioningErrorBox}
      {deleteBundleErrorBox && deleteBundleErrorBox}
      {screenIndex === SCREEN_INDEXES.ADD_DEVICES && (
        <CommissionAddDevicesScreen
          fromPivotOverView={props.fromPivotOverView}
          targetDonor={props.targetDonor}
          targetServices={props.targetServices}
          targetBundle={props.targetBundle}
          onClose={closeModal}
          onNext={onNext}
          setRecommBundleId={setRecommBundleId}
          terminateCommissionOpErrorCode={terminateCommissionOpErrorCode}
          setTerminateCommissionOpErrorCode={setTerminateCommissionOpErrorCode}
        />
      )}
      {screenIndex === SCREEN_INDEXES.CREATE_SITE && (
        <CommissionCreateSiteScreen
          onClose={closeModal}
          onNext={onNext}
          fromPivotOverView={props.fromPivotOverView}
          targetDonor={props.targetDonor}
          targetServices={props.targetServices}
          targetBundle={props.targetBundle}
          selectedDevices={selectedDevices}
          createBundleErrorCode={createBundleErrorCode}
          patchBundleErrorCode={patchBundleErrorCode}
          commissioningErrorCode={commissioningErrorCode}
          commissioningId={localCommissioningId}
          bundleId={newBundleId}
          recommBundleId={recommBundleId}
          retryCommissioningOp={retryCommissioningOp}
        />
      )}
      {screenIndex === SCREEN_INDEXES.IN_PROGRESS && (
        <RasterProgressScreen
          onClose={closeModal}
          onNext={onNext}
          customStatus={customStatus}
          commissioningProgress={commissioningProgress}
          commissioningId={localCommissioningId}
          getStatusErrorMsg={getStatusErrorMsg}
          clearTimeoutAndInterval={clearTimeoutAndInterval}
          setScreenIndex={setScreenIndex}
        />
      )}
      {screenIndex === SCREEN_INDEXES.SELECT_BEAM && (
        <CommissionBeamSelectionScreen
          onClose={closeModal}
          onNext={onNext}
          commissioningId={localCommissioningId}
          clearTimeoutAndInterval={clearTimeoutAndInterval}
          selectedDevices={selectedDevices}
          setScreenIndex={setScreenIndex}
        />
      )}
      {screenIndex === SCREEN_INDEXES.HBF_SERVICE && (
        <CommissionHBFServiceScreen
          onClose={closeModal}
          onNext={onNext}
          commissioningId={localCommissioningId}
          clearTimeoutAndInterval={clearTimeoutAndInterval}
          selectedDevices={selectedDevices}
        />
      )}
      {screenIndex === SCREEN_INDEXES.SUCCESS && (
        <CommissionSuccessScreen
          onClose={closeModal}
          onNext={onNext}
          clearTimeoutAndInterval={clearTimeoutAndInterval}
          timeCount={timeCount}
          actionsPageThis={props.actionsPageThis}
        />
      )}
    </Modal>
  );
};

export default CreateSiteModal;
